import { GoogleVisionProvider } from "./vision/googleVisionProvider";
import { extractJsonFromText, normalizeExtractionPayload } from "./vision/qwenVisionProvider";
import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp from "../../firebase/credenciales";
import { buildCloudFunctionUrl } from "../../config/firebaseEnv";

/**
 * Servicio de Extracción de Facturas - XML UBL SUNAT y PDF con Google Cloud Vision
 * Implementa procesamiento inteligente de documentos (IDP) para facturas peruanas.
 */

export const resolveGoogleVisionConfig = () => ({
  enabled: true,
});

const decodePdfText = (pdfBuffer) => {
  try {
    if (typeof Buffer !== "undefined") {
      if (pdfBuffer instanceof ArrayBuffer) {
        return Buffer.from(pdfBuffer).toString("utf8");
      }
      if (Buffer.isBuffer(pdfBuffer)) {
        return pdfBuffer.toString("utf8");
      }
      if (pdfBuffer && pdfBuffer.buffer instanceof ArrayBuffer) {
        return Buffer.from(pdfBuffer.buffer || pdfBuffer).toString("utf8");
      }
      if (pdfBuffer && (ArrayBuffer.isView(pdfBuffer) || pdfBuffer.constructor?.name?.includes("Array"))) {
        return Buffer.from(pdfBuffer).toString("utf8");
      }
    }

    if (typeof TextDecoder !== "undefined") {
      const decoder = new TextDecoder("utf-8", { fatal: false });
      return decoder.decode(pdfBuffer);
    }

    const runtimeGlobal = typeof window !== "undefined" && window ? window : null;
    if (runtimeGlobal && typeof runtimeGlobal.TextDecoder === "function") {
      const decoder = new runtimeGlobal.TextDecoder("utf-8", { fatal: false });
      return decoder.decode(pdfBuffer);
    }

    return "";
  } catch (error) {
    return "";
  }
};

const createGoogleVisionProvider = () => {
  const config = resolveGoogleVisionConfig();
  if (!config.enabled) return null;
  return new GoogleVisionProvider();
};

const extractTextFromPdfWithPdfJs = async (arrayBuffer) => {
  try {
    if (typeof window === "undefined") return "";

    const pdfjsLib = window.pdfjsLib || (await import("pdfjs-dist"));
    if (!pdfjsLib?.getDocument) return "";

    if (!window.pdfjsLib) {
      window.pdfjsLib = pdfjsLib;
      const v = pdfjsLib.version || "2.16.105";
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${v}/pdf.worker.min.js`;
    }

    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
    let fullText = "";

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const pageText = textContent?.items?.map((item) => item.str).join(" ") || "";
      if (pageText) {
        fullText = fullText ? `${fullText}\n${pageText}` : pageText;
      }
    }

    return fullText.trim();
  } catch (error) {
    console.warn("[FacturaExtractor] No se pudo extraer texto del PDF con PDF.js:", error);
    return "";
  }
};

const renderPdfToCanvasImageBase64 = async (arrayBuffer) => {
  try {
    if (typeof window === "undefined") return "";

    const pdfjsLib = window.pdfjsLib || (await import("pdfjs-dist"));
    if (!pdfjsLib?.getDocument) return "";

    if (!window.pdfjsLib) {
      window.pdfjsLib = pdfjsLib;
      const v = pdfjsLib.version || "2.16.105";
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${v}/pdf.worker.min.js`;
    }

    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
    if (!pdf || pdf.numPages === 0) return "";

    const page = await pdf.getPage(1);
    const scale = 2.0;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const context = canvas.getContext("2d");

    await page.render({ canvasContext: context, viewport }).promise;
    const dataUrl = canvas.toDataURL("image/png");
    return dataUrl.split(",")[1] || "";
  } catch (err) {
    console.warn("[FacturaExtractor] Error renderizando PDF a Canvas para Google Cloud Vision OCR:", err);
    return "";
  }
};

const extractTextWithGoogleVisionRestApi = async (base64Content, mimeType) => {
  if (!base64Content) return "";

  const apiKeys = [
    "AIzaSyDWd9RxWDR0sGszAi5BjfR75DQyPhcF8Jc",
    process.env.REACT_APP_GOOGLE_VISION_API_KEY,
    process.env.REACT_APP_FIREBASE_API_KEY
  ].filter(Boolean);

  for (const apiKey of apiKeys) {
    try {
      const response = await fetch(`https://vision.googleapis.com/v1/images:annotate?key=${apiKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [
            {
              image: { content: base64Content },
              features: [{ type: "DOCUMENT_TEXT_DETECTION" }]
            }
          ]
        })
      });

      if (response.ok) {
        const data = await response.json();
        const fullText = data?.responses?.[0]?.fullTextAnnotation?.text || "";
        if (fullText) {
          console.log("[FacturaExtractor] ✅ OCR completado exitosamente con Google Cloud Vision REST API");
          return fullText;
        }
      }
    } catch (err) {
      console.warn("[FacturaExtractor] Fallback REST Google Vision error:", err);
    }
  }

  return "";
};

const extractTextWithVisionCallable = async (base64Content, mimeType) => {
  // 1. Intentar Firebase Callable en us-central1 (Cloud Function desplegada en servidor)
  try {
    const functions = getFunctions(firebaseApp, "us-central1");
    const callable = httpsCallable(functions, "extractInvoiceTextWithVision");
    const response = await callable({ base64Content, mimeType });
    if (response?.data?.text) {
      console.log("[FacturaExtractor] ✅ OCR completado exitosamente vía Firebase Cloud Function extractInvoiceTextWithVision!");
      return response.data.text;
    }
  } catch (error) {
    console.warn("[FacturaExtractor] httpsCallable Google Vision:", error?.message || error);
  }

  // 2. Intentar REST API directa de Google Cloud Vision (fallback)
  const restText = await extractTextWithGoogleVisionRestApi(base64Content, mimeType);
  if (restText) return restText;

  // 3. Intentar endpoint HTTP directo de Cloud Function
  try {
    const url = buildCloudFunctionUrl("extractInvoiceTextWithVision");
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: { base64Content, mimeType } })
    });

    if (response.ok) {
      const data = await response.json();
      const text = data?.result?.text || data?.data?.text || data?.text || "";
      if (text) return text;
    }
  } catch (err) {
    console.warn("[FacturaExtractor] HTTP Cloud Function Google Vision no disponible:", err);
  }

  return "";
};

/**
 * Estructura estándar de factura extraída
 */
const FACTURA_TEMPLATE = {
  proveedor: {
    ruc: "",
    razonSocial: "",
    direccion: ""
  },
  comprobante: {
    tipo: "FACTURA", // FACTURA, BOLETA, NOTA_CREDITO, NOTA_DEBITO
    serie: "",
    numero: "",
    fechaEmision: "",
    fechaVencimiento: "",
    moneda: "PEN"
  },
  montos: {
    subtotal: 0,
    igv: 0,
    total: 0,
    descuentos: 0,
    otrosCargos: 0
  },
  items: [], // { descripcion, cantidad, unidad, precioUnitario, subtotal }
  cliente: {
    ruc: "",
    razonSocial: ""
  },
  metadatos: {
    fuente: "", // "XML_UBL" | "GEMINI_VISION"
    confianza: 0, // 0-100
    fechaProcesamiento: ""
  }
};

/**
 * Parser de XML UBL 2.1 (Factura Electrónica SUNAT)
 * @param {string} xmlContent - Contenido XML del comprobante
 * @returns {Object} Datos de la factura extraídos
 */
export const parseXmlUblSunat = (xmlContent) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, "text/xml");

    // Verificar errores de parsing
    const parseError = xmlDoc.querySelector("parsererror");
    if (parseError) {
      throw new Error("XML inválido: " + parseError.textContent);
    }

    // Función helper para obtener texto de nodos con namespace
    const getText = (selector, context = xmlDoc) => {
      const ctx = context || xmlDoc;
      if (!ctx) return "";

      const localName = selector.split(":").pop();
      const node = getByLocalName(localName, ctx);
      if (node?.textContent) return node.textContent.trim();

      // Fallbacks standard
      const selectors = [
        selector,
        selector.replace(/cbc:|cac:/g, ""),
        `*[local-name()='${localName}']`
      ];
      
      for (const sel of selectors) {
        try {
          const nodeFallback =
            (typeof ctx.querySelector === "function" ? ctx.querySelector(sel) : null) ||
            (typeof ctx.getElementsByTagName === "function"
              ? ctx.getElementsByTagName(localName)[0]
              : null);
          if (nodeFallback?.textContent) return nodeFallback.textContent.trim();
        } catch (e) { /* continuar */ }
      }
      return "";
    };

    // Buscar nodos por nombre local (ignorando namespace y capitalización)
    const getByLocalName = (localName, context = xmlDoc) => {
      const ctx = context || xmlDoc;
      if (!ctx || typeof ctx.getElementsByTagName !== "function") return null;
      const elements = ctx.getElementsByTagName("*");
      const lower = localName.toLowerCase();
      for (let el of elements) {
        const ln = el.localName || el.nodeName || "";
        if (ln.toLowerCase() === lower || ln.toLowerCase().split(":").pop() === lower) return el;
      }
      return null;
    };

    const getAllByLocalName = (localName, context = xmlDoc) => {
      const ctx = context || xmlDoc;
      if (!ctx || typeof ctx.getElementsByTagName !== "function") return [];
      const elements = ctx.getElementsByTagName("*");
      const result = [];
      const lower = localName.toLowerCase();
      for (let el of elements) {
        const ln = el.localName || el.nodeName || "";
        if (ln.toLowerCase() === lower || ln.toLowerCase().split(":").pop() === lower) result.push(el);
      }
      return result;
    };

    const getPartyData = (partyContainer) => {
      if (!partyContainer) {
        return { ruc: "", razonSocial: "", direccion: "" };
      }

      const partyNode = getByLocalName("Party", partyContainer) || partyContainer;
      const partyIdentification = getByLocalName("PartyIdentification", partyNode);
      const partyLegalEntity = getByLocalName("PartyLegalEntity", partyNode);
      const registrationAddress = getByLocalName("RegistrationAddress", partyNode);
      const postalAddress = getByLocalName("PostalAddress", partyNode);

      // UBL SUNAT suele enviar el RUC en PartyIdentification/cbc:ID (schemeID=6)
      const ruc =
        getText("ID", partyIdentification) ||
        getText("CompanyID", partyNode) ||
        getText("CustomerAssignedAccountID", partyContainer) ||
        getText("ID", partyNode) ||
        "";

      const razonSocial =
        getText("RegistrationName", partyLegalEntity) ||
        getText("Name", getByLocalName("PartyName", partyNode)) ||
        getText("RegistrationName", partyNode) ||
        getText("Name", partyNode) ||
        "";

      const direccion =
        getText("Line", getByLocalName("AddressLine", registrationAddress)) ||
        getText("Line", getByLocalName("AddressLine", postalAddress)) ||
        getText("StreetName", registrationAddress) ||
        getText("StreetName", postalAddress) ||
        getText("AddressLine", partyNode) ||
        "";

      return {
        ruc: String(ruc || "").trim(),
        razonSocial: String(razonSocial || "").trim(),
        direccion: String(direccion || "").trim(),
      };
    };

    // Tipo de documento
    const rootName = xmlDoc.documentElement?.localName || "";
    let tipoDoc = "FACTURA";
    if (rootName.includes("CreditNote")) tipoDoc = "NOTA_CREDITO";
    else if (rootName.includes("DebitNote")) tipoDoc = "NOTA_DEBITO";

    // Datos del proveedor (AccountingSupplierParty)
    const supplierParty = getByLocalName("AccountingSupplierParty");
    const supplierData = getPartyData(supplierParty);

    // Datos del cliente (AccountingCustomerParty)
    const customerParty = getByLocalName("AccountingCustomerParty");
    const customerData = getPartyData(customerParty);

    // Serie y número del comprobante
    let fullId = "";
    
    // 1. Buscar hijo directo <cbc:ID> o <ID> del nodo raíz <Invoice> / <CreditNote> / <DebitNote>
    const root = xmlDoc.documentElement;
    if (root) {
      for (let i = 0; i < root.children.length; i++) {
        const child = root.children[i];
        if (child.localName === "ID") {
          const val = child.textContent?.trim() || "";
          if (val && !val.includes("Signature") && !val.includes("Sign")) {
            fullId = val;
            break;
          }
        }
      }
    }

    // 2. Si no se encontró en el hijo directo, buscar patrón de Serie-Correlativo en el XML (ej. F001-00012345, B001-123, SB01-0641494330)
    if (!fullId) {
      const matchId = xmlContent.match(/<(?:cbc:)?ID[^>]*>\s*([A-Z0-9]{3,6}[-\s]\d{3,20})\s*<\/(?:cbc:)?ID>/i);
      if (matchId) fullId = matchId[1];
    }

    // 3. Fallback final
    if (!fullId) {
      const idNode = getByLocalName("ID");
      fullId = idNode?.textContent?.trim() || "";
    }

    const [serie, numero] = fullId.includes("-") 
      ? fullId.split("-") 
      : (fullId.includes(" ") ? fullId.split(" ") : ["", fullId]);

    // Fechas
    const fechaEmision = getText("IssueDate") || "";
    const fechaVencimiento = getText("DueDate") || getText("PaymentDueDate") || fechaEmision;

    // Moneda
    const monedaNode = getByLocalName("DocumentCurrencyCode");
    const moneda = monedaNode?.textContent?.trim() || "PEN";

    // Montos
    const legalMonetary = getByLocalName("LegalMonetaryTotal");
    const taxTotal = getByLocalName("TaxTotal");
    
    const subtotal = parseFloat(getText("LineExtensionAmount", legalMonetary) || getText("TaxableAmount", taxTotal) || "0");
    const total = parseFloat(getText("PayableAmount", legalMonetary) || getText("TaxInclusiveAmount", legalMonetary) || "0");
    const igv = parseFloat(getText("TaxAmount", taxTotal) || "0");

    // Items (InvoiceLine o CreditNoteLine o DebitNoteLine)
    const lineNodes = getAllByLocalName("InvoiceLine").length > 0 
      ? getAllByLocalName("InvoiceLine")
      : getAllByLocalName("CreditNoteLine").length > 0
        ? getAllByLocalName("CreditNoteLine")
        : getAllByLocalName("DebitNoteLine");

    const items = lineNodes.map((line) => {
      const descripcion = getText("Description", line) || getByLocalName("Description", line)?.textContent?.trim() || "";
      const cantidad = parseFloat(getText("InvoicedQuantity", line) || getText("CreditedQuantity", line) || getText("DebitedQuantity", line) || "1");
      const precioUnitario = parseFloat(getText("PriceAmount", line) || "0");
      const lineTotal = parseFloat(getText("LineExtensionAmount", line) || "0");
      
      let unidad = "NIU";
      try {
        const unitEl = line.querySelector("[unitCode]") || getByLocalName("InvoicedQuantity", line) || getByLocalName("CreditedQuantity", line) || getByLocalName("DebitedQuantity", line);
        if (unitEl && typeof unitEl.getAttribute === "function") {
          unidad = unitEl.getAttribute("unitCode") || "NIU";
        }
      } catch (e) {}

      return {
        descripcion,
        cantidad,
        unidad,
        precioUnitario,
        subtotal: lineTotal
      };
    });

    // Extracción de documento modificado y motivo (para Nota de Crédito / Débito)
    let documentoModificado = "";
    let motivo = "";
    if (rootName.includes("CreditNote") || rootName.includes("DebitNote")) {
      const referenceIdNode = getByLocalName("ReferenceID") || getByLocalName("InvoiceDocumentReference");
      if (referenceIdNode) {
        documentoModificado = getText("ID", referenceIdNode) || referenceIdNode.textContent?.trim() || "";
      }
      if (!documentoModificado) {
        const matchRef = xmlContent.match(/<(?:cbc:)?ReferenceID[^>]*>\s*([A-Z0-9]{3,6}[-\s]\d{3,20})\s*<\/(?:cbc:)?ReferenceID>/i) ||
                         xmlContent.match(/<(?:cac:)?InvoiceDocumentReference>[\s\S]*?<(?:cbc:)?ID[^>]*>\s*([A-Z0-9]{3,6}[-\s]\d{3,20})\s*<\/(?:cbc:)?ID>/i);
        if (matchRef) documentoModificado = matchRef[1];
      }

      const discrepancyNode = getByLocalName("DiscrepancyResponse");
      if (discrepancyNode) {
        motivo = getText("Description", discrepancyNode) || "";
      }
      if (!motivo) {
        const matchMotivo = xmlContent.match(/<(?:cbc:)?Description[^>]*>\s*([^<]{3,120})\s*<\/(?:cbc:)?Description>/i);
        if (matchMotivo) motivo = matchMotivo[1];
      }
    }

    return {
      ...FACTURA_TEMPLATE,
      proveedor: {
        ruc: supplierData.ruc,
        razonSocial: supplierData.razonSocial,
        direccion: supplierData.direccion
      },
      comprobante: {
        tipo: tipoDoc,
        serie,
        numero,
        fechaEmision,
        fechaVencimiento,
        moneda,
        documentoModificado: String(documentoModificado || "").trim().toUpperCase(),
        motivo: String(motivo || "").trim()
      },
      montos: {
        subtotal,
        igv,
        total,
        descuentos: 0,
        otrosCargos: 0
      },
      items,
      cliente: {
        ruc: customerData.ruc,
        razonSocial: customerData.razonSocial
      },
      metadatos: {
        fuente: "XML_UBL",
        confianza: 100,
        fechaProcesamiento: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error("[FacturaExtractor] Error parseando XML:", error);
    throw new Error("No se pudo procesar el XML: " + error.message);
  }
};

/**
 * Intenta extraer XML embebido de un PDF de factura electrónica
 * @param {ArrayBuffer} pdfBuffer - Buffer del PDF
 * @returns {string|null} Contenido XML o null si no existe
 */
export const extractXmlFromPdf = async (pdfBuffer) => {
  try {
    // Convertir a texto para buscar el XML embebido
    const pdfText = decodePdfText(pdfBuffer);

    // Buscar patrones de XML UBL embebido
    const xmlPatterns = [
      /<Invoice[\s\S]*?<\/Invoice>/,
      /<CreditNote[\s\S]*?<\/CreditNote>/,
      /<DebitNote[\s\S]*?<\/DebitNote>/,
      /<%3C%3Fxml[\s\S]*?Invoice%3E/,  // URL encoded
    ];

    for (const pattern of xmlPatterns) {
      const match = pdfText.match(pattern);
      if (match) {
        let xmlContent = match[0];
        // Decodificar si está URL encoded
        if (xmlContent.includes("%3C")) {
          xmlContent = decodeURIComponent(xmlContent);
        }
        return xmlContent;
      }
    }

    // Buscar streams que puedan contener XML
    const streamMatches = pdfText.match(/stream[\s\S]*?endstream/g) || [];
    for (const stream of streamMatches) {
      if (stream.includes("Invoice") || stream.includes("cbc:") || stream.includes("cac:")) {
        const xmlMatch = stream.match(/<\?xml[\s\S]*?<\/(?:Invoice|CreditNote|DebitNote)>/);
        if (xmlMatch) return xmlMatch[0];
      }
    }

    return null;
  } catch (error) {
    console.warn("[FacturaExtractor] No se encontró XML embebido en PDF:", error);
    return null;
  }
};

/**
 * Extrae datos de factura usando Google Cloud Vision OCR o PDF.js
 * @param {string} base64Content - Contenido en base64
 * @param {string} mimeType - Tipo MIME
 * @param {ArrayBuffer} pdfArrayBuffer - ArrayBuffer opcional del PDF
 * @returns {Object} Datos de la factura extraídos
 */
export const extractFacturaWithGoogleVision = async (base64Content, mimeType, pdfArrayBuffer = null) => {
  try {
    let text = "";
    let isPdfText = false;
    let imageBase64ForOcr = base64Content;
    let imageMimeForOcr = mimeType;

    // 1. Si es PDF, intentar extraer texto nativo digital para respaldo y renderizar a Canvas PNG para Qwen Vision AI
    if (pdfArrayBuffer && mimeType === "application/pdf") {
      text = await extractTextFromPdfWithPdfJs(pdfArrayBuffer);
      
      console.log("[FacturaExtractor] Renderizando PDF a Canvas PNG para Qwen Vision AI...");
      const canvasBase64 = await renderPdfToCanvasImageBase64(pdfArrayBuffer);
      if (canvasBase64) {
        imageBase64ForOcr = canvasBase64;
        imageMimeForOcr = "image/png";
      }
    }

    // 2. Intentar primero con Qwen Vision (Alibaba Cloud Singapore Multimodal AI) para máxima precisión
    if (imageBase64ForOcr && imageMimeForOcr.startsWith("image/")) {
      try {
        const functions = getFunctions(firebaseApp, "us-central1");
        const callable = httpsCallable(functions, "extractInvoiceTextWithQwen");
        const response = await callable({ base64Content: imageBase64ForOcr, mimeType: imageMimeForOcr });
        const responseText = response?.data?.text || "";

        if (responseText) {
          const parsed = extractJsonFromText(responseText);
          const normalized = normalizeExtractionPayload(parsed);
          if (normalized?.proveedor?.ruc || normalized?.comprobante?.serie || normalized?.montos?.total > 0) {
            console.log("[FacturaExtractor] ✅ Extracción completada exitosamente vía Qwen Vision (Alibaba Cloud)!");
            return {
              ...normalized,
              metadatos: {
                fuente: "QWEN_VISION",
                confianza: 98,
                fechaProcesamiento: new Date().toISOString()
              }
            };
          }
        }
      } catch (qwenErr) {
        console.warn("[FacturaExtractor] Qwen Vision no disponible, cayendo a Google Vision OCR:", qwenErr?.message || qwenErr);
      }
    }

    // 3. Respaldo: Enviar a Google Cloud Vision OCR si Qwen no está disponible
    if (!text) {
      text = await extractTextWithVisionCallable(imageBase64ForOcr, imageMimeForOcr);
    }

    if (text) {
      const provider = createGoogleVisionProvider();
      if (provider) {
        const extractedData = await provider.extractStructuredData({ text });

        // Intentar extraer documento modificado por expresión regular si es Nota de Crédito / Débito
        let docMod = extractedData.comprobante?.documentoModificado || "";
        if (!docMod) {
          const matchRef = text.match(/(?:documento|comprobante|factura)\s*(?:que\s*modifica|modificado|afectado|de\s*referencia|afectad[oa])\s*:?\s*([A-Z0-9]{3,6}[-\s]\d{3,20})/i) ||
                           text.match(/(?:modifica\s*a|referencia)\s*:?\s*([A-Z0-9]{3,6}[-\s]\d{3,20})/i);
          if (matchRef) docMod = matchRef[1].replace(/\s+/, "-").toUpperCase();
        }

        return {
          ...FACTURA_TEMPLATE,
          proveedor: extractedData.proveedor || FACTURA_TEMPLATE.proveedor,
          comprobante: {
            ...(extractedData.comprobante || FACTURA_TEMPLATE.comprobante),
            documentoModificado: docMod
          },
          montos: extractedData.montos || FACTURA_TEMPLATE.montos,
          items: extractedData.items || [],
          cliente: extractedData.cliente || FACTURA_TEMPLATE.cliente,
          metadatos: {
            fuente: isPdfText ? "PDF_TEXT" : "GOOGLE_VISION",
            confianza: extractedData.metadatos?.confianza || (isPdfText ? 85 : 75),
            fechaProcesamiento: new Date().toISOString()
          }
        };
      }
    }

    // Si Google Vision OCR / PDF.js no obtienen texto, lanzar error para activar modo manual limpio
    throw new Error("OCR_NO_DISPONIBLE");
  } catch (error) {
    if (error.message === "OCR_NO_DISPONIBLE" || error.message === "RATE_LIMIT" || error.message === "CUOTA_AGOTADA") {
      console.info("[FacturaExtractor] Activando modo manual por OCR_NO_DISPONIBLE");
      throw error;
    }
    console.error("[FacturaExtractor] Error procesando documento:", error);
    throw new Error(error.message || "No se pudo extraer texto del documento con Google Vision");
  }
};

const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    // Si no es un Blob/File real (por ejemplo, en tests de Jest/JSDOM con mocks) o no está en el navegador
    const isBlob = typeof Blob !== "undefined" && file instanceof Blob;
    if (typeof FileReader === "undefined" || !isBlob) {
      // Fallback seguro para entorno Node.js/Jest
      if (file instanceof ArrayBuffer || file instanceof Buffer) {
        return resolve(Buffer.from(file).toString("base64"));
      }
      if (file && (file.buffer instanceof ArrayBuffer || file.buffer instanceof Buffer)) {
        return resolve(Buffer.from(file.buffer).toString("base64"));
      }
      if (file && typeof file.arrayBuffer === "function") {
        file.arrayBuffer().then(buf => {
          resolve(Buffer.from(buf).toString("base64"));
        }).catch(() => resolve(""));
        return;
      }
      return resolve("");
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = typeof result === "string" ? result.split(",")[1] || "" : "";
      resolve(base64);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

/**
 * Función principal: procesa un archivo de factura (XML o PDF/imagen)
 * @param {File} file - Archivo a procesar
 * @returns {Object} Datos de la factura extraídos
 */
export const procesarArchivoFactura = async (file) => {
  const fileName = file.name.toLowerCase();
  const mimeType = file.type;

  // Leer archivo como ArrayBuffer y Base64 de forma segura
  const arrayBuffer = await file.arrayBuffer();
  const base64 = await convertFileToBase64(file);

  // Caso 1: Es un archivo XML directo
  if (fileName.endsWith(".xml") || mimeType === "text/xml" || mimeType === "application/xml") {
    const decoder = new TextDecoder("utf-8");
    const xmlContent = decoder.decode(arrayBuffer);
    return parseXmlUblSunat(xmlContent);
  }

  // Caso 2: Es un PDF - intentar extraer XML embebido primero
  if (fileName.endsWith(".pdf") || mimeType === "application/pdf") {
    const embeddedXml = await extractXmlFromPdf(arrayBuffer);
    
    if (embeddedXml) {
      console.log("[FacturaExtractor] XML embebido encontrado en PDF");
      return parseXmlUblSunat(embeddedXml);
    }

    console.log("[FacturaExtractor] Procesando PDF con texto embebido y OCR de respaldo");
    return extractFacturaWithGoogleVision(base64, "application/pdf", arrayBuffer);
  }

  // Caso 3: Es una imagen (JPG, PNG)
  if (mimeType.startsWith("image/")) {
    console.log("[FacturaExtractor] Procesando imagen con Google Vision");
    return extractFacturaWithGoogleVision(base64, mimeType);
  }

  throw new Error(`Tipo de archivo no soportado: ${mimeType}. Use XML, PDF o imagen (JPG/PNG).`);
};

/**
 * Valida los datos extraídos de una factura
 * @param {Object} factura - Datos de factura extraídos
 * @returns {Object} { esValido: boolean, errores: string[], advertencias: string[] }
 */
export const validarFacturaExtraida = (factura) => {
  const errores = [];
  const advertencias = [];

  // Validar RUC proveedor (11 dígitos peruano: inicia con 10, 15, 16, 17 o 20)
  const rucProv = factura.proveedor?.ruc || "";
  if (!rucProv || !/^\d{11}$/.test(rucProv)) {
    errores.push("RUC del proveedor inválido o no encontrado (debe tener 11 dígitos)");
  } else if (!/^(10|15|16|17|20)\d{9}$/.test(rucProv)) {
    advertencias.push("El RUC del proveedor no coincide con el formato estándar peruano (debe iniciar con 10, 15, 16, 17 o 20)");
  }

  // Validar serie y número
  if (!factura.comprobante?.serie) {
    errores.push("Serie del comprobante no encontrada");
  }
  if (!factura.comprobante?.numero) {
    errores.push("Número del comprobante no encontrado");
  }

  // Validar fecha de emisión
  if (!factura.comprobante?.fechaEmision) {
    errores.push("Fecha de emisión no encontrada");
  }

  // Validar montos
  if (!factura.montos?.total || factura.montos.total <= 0) {
    errores.push("Total del comprobante inválido");
  }

  // Advertencias
  if (!factura.proveedor?.razonSocial) {
    advertencias.push("Razón social del proveedor no encontrada");
  }
  if (!factura.comprobante?.fechaVencimiento) {
    advertencias.push("Fecha de vencimiento no encontrada, se usará fecha de emisión + 30 días");
  }
  if (factura.metadatos?.confianza < 80) {
    advertencias.push(`Confianza de extracción: ${factura.metadatos.confianza}%. Verifique los datos.`);
  }

  return {
    esValido: errores.length === 0,
    errores,
    advertencias
  };
};

export const resolveQwenConfig = () => {
  const previousGlobal = typeof window !== "undefined" && window.__ENV__ ? window.__ENV__ : {};
  return {
    apiKey: previousGlobal.REACT_APP_QWEN_API_KEY || process.env.REACT_APP_QWEN_API_KEY || "",
    baseUrl: previousGlobal.REACT_APP_QWEN_BASE_URL || process.env.REACT_APP_QWEN_BASE_URL || "https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions",
    model: previousGlobal.REACT_APP_QWEN_VISION_MODEL || process.env.REACT_APP_QWEN_VISION_MODEL || "qwen-vl-plus",
  };
};

const facturaExtractorService = {
  procesarArchivoFactura,
  parseXmlUblSunat,
  extractXmlFromPdf,
  extractFacturaWithGoogleVision,
  validarFacturaExtraida,
  resolveQwenConfig
};

export default facturaExtractorService;
